// Broadnet → Terminales: TID/MID/usuario Broadnet por cada caja (clave cifrada, write-only).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MonitorSmartphone, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { BnTerminal, Caja, Campo, Modal, cajasActivas, bnTerminalSet, bnTerminalesListar, fieldCls } from './shared';

export function TerminalesBroadnet() {
  const qc = useQueryClient();
  const { data: terminales = [], isFetching } = useQuery<BnTerminal[]>({
    queryKey: ['pos-bn-terminales'], queryFn: bnTerminalesListar, staleTime: 0,
  });
  const { data: cajas = [] } = useQuery<Caja[]>({ queryKey: ['pos-cajas-activas'], queryFn: cajasActivas });

  const [cajaId, setCajaId] = useState<string | null>(null);
  const [tid, setTid] = useState('');
  const [mid, setMid] = useState('');
  const [cajero, setCajero] = useState('');
  const [clave, setClave] = useState('');
  const [abierto, setAbierto] = useState(false);

  const actual = (id: string) => terminales.find((t) => t.caja_id === id) || null;

  const abrir = (caja: Caja) => {
    const t = actual(caja.id);
    setCajaId(caja.id); setTid(t?.tid ?? ''); setMid(t?.mid ?? ''); setCajero(t?.cajero_bn ?? ''); setClave('');
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      if (!cajaId) throw new Error('Selecciona una caja');
      return bnTerminalSet({ caja_id: cajaId, tid, mid, cajero, clave: clave || null });
    },
    onSuccess: () => { toast.success('Terminal guardado (clave cifrada)'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-bn-terminales'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const configuradas = terminales.filter((t) => t.tid || t.mid || t.clave_configurada).length;

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><MonitorSmartphone className="w-5 h-5" /> Broadnet · Terminales</h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Cajas ({cajas.length}) · con terminal Broadnet {configuradas}
          {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Caja</th><th className="py-2 pr-3">TID</th>
              <th className="py-2 pr-3">MID</th><th className="py-2 pr-3">Cajero Broadnet</th><th className="py-2 pr-3">Clave</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {cajas.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin cajas activas — crea una en POS → Configuración → Cajas</td></tr>}
              {cajas.map((c, i) => {
                const t = actual(c.id);
                return (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{c.nombre}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{t?.tid || '—'}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{t?.mid || '—'}</td>
                    <td className="py-2 pr-3 text-xs">{t?.cajero_bn || '—'}</td>
                    <td className="py-2 pr-3 text-xs">{t?.clave_configurada ? <span className="text-green-700 dark:text-green-400">✓ configurada</span> : <span className="text-muted-foreground">sin configurar</span>}</td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => abrir(c)}><Pencil className="w-3.5 h-3.5 mr-1" /> {t ? 'Editar' : 'Configurar'}</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Terminal Broadnet" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Caja">
              <input className={fieldCls} value={cajas.find((c) => c.id === cajaId)?.nombre ?? ''} disabled />
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="TID"><input className={fieldCls} value={tid} onChange={(e) => setTid(e.target.value)} /></Campo>
              <Campo label="MID"><input className={fieldCls} value={mid} onChange={(e) => setMid(e.target.value)} /></Campo>
            </div>
            <Campo label="Cajero Broadnet"><input className={fieldCls} value={cajero} onChange={(e) => setCajero(e.target.value)} /></Campo>
            <Campo label="Clave (write-only — dejar en blanco = conservar)">
              <input type="password" autoComplete="new-password" className={fieldCls} value={clave} onChange={(e) => setClave(e.target.value)} />
            </Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
